import { ArrowDown, ArrowRight, ArrowUp, CheckCircle2, Loader2, Pencil, X } from "lucide-react";
import type { DocumentSectionKind } from "../../services/api";
import type { DataDomain } from "../../services/dataDomain";
import type { ChartType } from "../../types";
import { isPlanStop } from "../canvas/canvasModel";
import { PlanOutcome } from "../canvas/PlanOutcome";
import { DOCUMENT_SECTIONS, documentSectionPrompt } from "../canvas/prompts";
import { QuestionField } from "../canvas/QuestionField";
import {
  instructionProblem,
  isDataKind,
  MAX_INSTRUCTION_CHARS,
  SECTION_KINDS,
  type DocumentRow,
} from "./documentModel";

const CHART_NAMES: Record<ChartType, string> = {
  bar: "bar chart",
  line: "line chart",
  pie: "pie chart",
  scalar: "single figure",
  table: "table",
};

/** How a planned data section will appear, which the kind decides over the plan. */
function shownAs(row: DocumentRow): string {
  if (row.kind === "headline_number") return "single figure";
  if (row.kind === "table") return "table";
  return row.panel ? CHART_NAMES[row.panel.chartType] : "chart";
}

interface DocumentSectionRowProps {
  row: DocumentRow;
  index: number;
  count: number;
  domain: DataDomain | null;
  /** After Create, a data row in draft is asked on its own. */
  started: boolean;
  /** Composing or composed: nothing can change. */
  locked: boolean;
  onInstruction: (instruction: string) => void;
  onKind: (kind: DocumentSectionKind) => void;
  onMove: (offset: -1 | 1) => void;
  onRemove: () => void;
  onAsk: () => void;
  onRetry: () => void;
  onEdit: () => void;
  onChooseOption: (option: string) => void;
  onKeepCandidate: (index: number) => void;
}

/** One full-width section of a report or artifact canvas. */
export function DocumentSectionRow({
  row,
  index,
  count,
  domain,
  started,
  locked,
  onInstruction,
  onKind,
  onMove,
  onRemove,
  onAsk,
  onRetry,
  onEdit,
  onChooseOption,
  onKeepCandidate,
}: DocumentSectionRowProps) {
  const copy = DOCUMENT_SECTIONS[row.kind];
  const label = `Section ${index + 1}`;
  const data = isDataKind(row.kind);
  const busy = row.phase === "planning";
  const canAsk = data && started && instructionProblem(row.instruction) === null;

  return (
    <section
      className={`document-row ${row.phase} ${data ? "data" : "written"}`}
      aria-label={`${label}: ${copy.label}`}
      aria-busy={busy}
    >
      <div className="document-row-kind">
        <span className="document-row-index" aria-hidden="true">
          {index + 1}
        </span>
        <label>
          <span className="sr-only">Type of {label}</span>
          <select
            value={row.kind}
            disabled={locked || busy}
            onChange={(event) => onKind(event.target.value as DocumentSectionKind)}
          >
            {SECTION_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {DOCUMENT_SECTIONS[kind].label}
              </option>
            ))}
          </select>
        </label>
        <small>{copy.description}</small>
      </div>

      <div className="document-row-body">
        {row.phase === "draft" && (
          <div className="document-row-draft">
            <QuestionField
              label={`Instruction for ${label}`}
              value={row.instruction}
              prompt={documentSectionPrompt(row.kind, domain)}
              maxLength={MAX_INSTRUCTION_CHARS}
              disabled={locked}
              onChange={onInstruction}
              onSubmit={canAsk && !locked ? onAsk : undefined}
            />
            {canAsk && !locked && (
              <button
                type="button"
                className="button primary small"
                onClick={onAsk}
                aria-label={`Ask ${label}`}
              >
                Ask <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}

        {busy && (
          <div className="canvas-tile-status" role="status">
            <Loader2 size={18} className="spin" aria-hidden="true" />
            <span>Finding this in the semantic model…</span>
          </div>
        )}

        {row.phase === "planned" && row.panel && (
          <div className="document-row-planned">
            <CheckCircle2 size={18} aria-hidden="true" />
            <div>
              <strong>{row.panel.title}</strong>
              <small>
                Shown as a {shownAs(row)} · “{row.instruction}”
              </small>
            </div>
            {!locked && (
              <button
                type="button"
                className="button tertiary small"
                onClick={onEdit}
                aria-label={`Edit the question in ${label}`}
              >
                <Pencil size={13} /> Edit
              </button>
            )}
          </div>
        )}

        {isPlanStop(row.phase) && (
          <PlanOutcome
            phase={row.phase}
            label={label}
            question={row.instruction}
            message={row.message}
            options={row.options}
            candidates={row.candidates}
            retryable={row.retryable}
            onChooseOption={onChooseOption}
            onKeepCandidate={onKeepCandidate}
            onRetry={onRetry}
            onEdit={onEdit}
          />
        )}
      </div>

      <div className="document-row-actions">
        <button
          type="button"
          className="icon-button"
          onClick={() => onMove(-1)}
          disabled={locked || index === 0}
          aria-label={`Move ${label} up`}
          title="Move up"
        >
          <ArrowUp size={15} />
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={() => onMove(1)}
          disabled={locked || index === count - 1}
          aria-label={`Move ${label} down`}
          title="Move down"
        >
          <ArrowDown size={15} />
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={onRemove}
          disabled={locked || busy || count <= 1}
          aria-label={`Remove ${label}`}
          title="Remove section"
        >
          <X size={15} />
        </button>
      </div>
    </section>
  );
}
